/**
 * Caddy Cart - WordPress Interactivity API Implementation
 *
 * Implements the cart store using WordPress native Interactivity API
 * with optimistic updates and WooCommerce Store API integration.
 *
 * @since 2.1.3
 */


import { store, getContext } from '@wordpress/interactivity';

// Import shared utilities
import { formatPrice, formatPriceSmart, convertStoreApiPrice } from './core/shared/formatters.js';
import { getCaddyNonce, getCaddyRestUrl, getStoreApiNonce, getStoreApiUrl, storeApiFetch, isSeededCartFresh, fetchCart, refreshNonceFromResponse } from './core/shared/api.js';
import { convertStoreApiItemToCaddyFormat, convertCartItems } from './core/shared/converters.js';
import { slideUp, slideDown } from './core/shared/ui-utils.js';
import { getWooCommercePlaceholderImage, CADDY_IMAGE_FALLBACK, decodeHTMLEntitiesDeep, findAddToCartForms } from './core/shared/dom-utils.js';
import { handlers } from './handlers/index.js';

// Import coupon functionality
import { initializeCouponDrawer, initializeCouponForm, updateAppliedCouponsDisplay } from './core/coupons/index.js';

// Flag to prevent duplicate recommendations updates during moveToCart
// Now handled via window._caddyMovingToCart in SFL module

// Track if initial cart load has completed
let initialCartLoadComplete = false;

// Track previous subtotal for free shipping meter updates
let prevFreeShippingSubtotal;

function getRecommendationLabel(key, fallback) {
	const state = store('caddy/cart')?.state;
	if (state?.i18n?.[key]) {
		return state.i18n[key];
	}

	const container = document.querySelector('.cc-cart-container[data-label-add-to-cart]');
	if (!container?.dataset) {
		return fallback;
	}

	switch (key) {
		case 'addToCart':
			return container.dataset.labelAddToCart || fallback;
		case 'seeOptions':
			return container.dataset.labelSeeOptions || fallback;
		case 'viewProducts':
			return container.dataset.labelViewProducts || fallback;
		default:
			return fallback;
	}
}

function ensureI18nState() {
	const cartStore = store('caddy/cart');
	if (!cartStore?.state) {
		return;
	}

	if (!cartStore.state.i18n) {
		cartStore.state.i18n = {};
	}

	cartStore.state.i18n.addToCart = cartStore.state.i18n.addToCart || getRecommendationLabel('addToCart', 'Add to cart');
	cartStore.state.i18n.seeOptions = cartStore.state.i18n.seeOptions || getRecommendationLabel('seeOptions', 'Select options');
	cartStore.state.i18n.viewProducts = cartStore.state.i18n.viewProducts || getRecommendationLabel('viewProducts', 'View products');
}

// Define the cart store

const cartStore = store('caddy/cart', {
	// State is provided by wp_interactivity_state() in PHP
	state: {
		// isLoading is writable state from PHP initial state - no getter needed
		get savedItemsCount() {
			const cartState = store('caddy/cart').state;
			return (cartState.savedItems && cartState.savedItems.length) || 0;
		},
		get freeShippingRemainingFormatted() {
			return calculateFreeShippingRemainingFormatted();
		},
		get freeShippingPercentage() {
			return calculateFreeShippingPercentage();
		},
		get freeShippingAchieved() {
			return calculateFreeShippingAchieved();
		},
		// Recommendations slider state getters
		get recommendationTransform() {
			const cartState = store('caddy/cart').state;
			const index = cartState.recommendationIndex || 0;
			const dragOffset = cartState._dragOffset || 0;
			// Each slide is 100% width, so translate by -100% per slide, plus any drag offset in px
			if (dragOffset !== 0) {
				return `translateX(calc(-${index * 100}% + ${dragOffset}px))`;
			}
			return `translateX(-${index * 100}%)`;
		},
		get recommendationSliderWidth() {
			// Width is handled by flex layout, no explicit width needed
			return 'auto';
		},
		get isFirstRecommendation() {
			const cartState = store('caddy/cart').state;
			const index = cartState.recommendationIndex || 0;
			return index === 0;
		},
		get isLastRecommendation() {
			const cartState = store('caddy/cart').state;
			const index = cartState.recommendationIndex || 0;
			const totalSlides = (cartState.recommendations && cartState.recommendations.length) || 0;
			return index >= totalSlides - 1;
		}
	},

	// Actions for cart interactions
	actions: {
		/**
		 * Toggle cart visibility
		 */
		async toggleCart() {
			const { state, actions } = store('caddy/cart');

			if (state.isOpen) {
				// Cart is open, close it
				actions.closeCart();
			} else {
				// Cart is closed, open it and fetch fresh data
				await actions.openCart();
			}
		},

		/**
		 * Keyboard support for the launcher (role="button"): Enter/Space toggles
		 */
		handleCompassKeydown(event) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				const { actions } = store('caddy/cart');
				actions.toggleCart();
			}
		},

		/**
		 * Swap a failed cart, recommendation, or saved-item image for the
		 * inline placeholder.
		 */
		onImageError(event) {
			const img = event.target;
			if (!img || img.getAttribute('src') === CADDY_IMAGE_FALLBACK) {
				return;
			}
			img.removeAttribute('srcset');
			img.src = CADDY_IMAGE_FALLBACK;

			const context = getContext();
			if (context) {
				if (context.item) {
					if ('image' in context.item) context.item.image = CADDY_IMAGE_FALLBACK;
					if ('thumbnailImage' in context.item) context.item.thumbnailImage = CADDY_IMAGE_FALLBACK;
				}
				if (context.rec && 'image' in context.rec) {
					context.rec.image = CADDY_IMAGE_FALLBACK;
				}
			}
		},

		/**
		 * Open cart and refresh data from Store API
		 * @param {string} targetTab - Optional tab to switch to ('cart' or 'saves')
		 */
		async openCart(targetTab = 'cart', skipFetch = false) {
			const { state } = store('caddy/cart');

			// Open cart immediately - shows server-rendered content instantly
			state.isOpen = true;
			document.body.classList.add('cc-window-open');

			// Always update empty class based on current state when opening
			updateCartEmptyClass(state.cartCount);

			// Always refresh recommendations when cart opens so results match current cart contents.
			if (state.recommendations !== undefined) {
				refreshRecommendationsFromServer(state);
			} else {
				// Legacy module path
				scheduleRecommendationsPrefetch();
			}

			// Skip fetch if we already have cart data from initial load
			// Cart operations (add/remove/update) handle their own fetches
			if (!skipFetch && !initialCartLoadComplete) {
				try {
					const cartResponse = await storeApiFetch('cart');

					if (cartResponse.ok) {
						const cartData = await cartResponse.json();

						// Check if cart actually changed (count or totals)
						const serverTotal = cartData.totals?.total_price || '0';
						const localTotal = String(Math.round((state.cartSubtotal || 0) * 100));
						if (state.cartCount !== cartData.items_count || serverTotal !== localTotal) {
							// Convert Store API format to Caddy format
							const caddyItems = convertCartItems(cartData.items);

							state.items = caddyItems;
							state.cartCount = cartData.items_count;
							state.isItemSingular = cartData.items_count === 1;
							updateCartTotals(state, cartData);
							updateCartEmptyClass(state.cartCount);
							updateCartWidgetCount(state.cartCount);
							state.coupons = cartData.coupons || [];

							// Update DOM elements with proper CSS classes based on sale status
							setTimeout(() => updateCartItemSaleClasses(caddyItems), 100);

							// Refresh recommendations now that cart changed
							initializeRecommendations();
						}
					}
				} catch (error) {
					// Silently fail - user still sees server-rendered cart
					console.warn('Caddy: cart operation failed:', error);
				}
			}

			// Switch to the appropriate tab
			setTimeout(() => {
				if (targetTab === 'saves' && window._caddySwitchToSavedTab) {
					window._caddySwitchToSavedTab();
				} else if (window._caddySwitchToCartTab) {
					window._caddySwitchToCartTab();
				}
			}, 50);
		},

		/**
		 * Close cart
		 */
		closeCart() {
			const { state } = store('caddy/cart');
			state.isOpen = false;
			document.body.classList.remove('cc-window-open');
		},

		/**
		 * Header back button: close cart when on cart tab, go back to cart on other tabs
		 */
		handleHeaderBack() {
			const activeNonCart = document.querySelector('.cc-screen-tab.cc-tab-active, .cc-screen-tab.cc-active:not(#cc-cart)');
			if (activeNonCart && activeNonCart.id !== 'cc-cart') {
				if (window._caddySwitchToCartTab) {
					window._caddySwitchToCartTab();
				} else {
					switchToTab('#cc-cart');
				}
			} else {
				store('caddy/cart').actions.closeCart();
			}
		},

		/**
		 * Increase item quantity
		 */
		async increaseQuantity() {
			const context = getContext();
			const cartKey = context.item.cartKey;
			const currentQuantity = context.item.quantity;
			if (context.item.isAtMaxQty) return;
			const newQuantity = currentQuantity + 1;
			const { state } = store('caddy/cart');

			// Find the item index first
			const itemIndex = state.items.findIndex(item => item.cartKey === cartKey);

			// Block qty changes on fixed-qty bundled items
			if (itemIndex !== -1 && state.items[itemIndex].hideQuantityButtons) {
				return;
			}

			// Respect max quantity
			if (itemIndex !== -1 && state.items[itemIndex].maxQuantity && newQuantity > state.items[itemIndex].maxQuantity) {
				return;
			}

			// Prevent rapid clicks - check if this item is already updating
			if (itemIndex !== -1 && state.items[itemIndex].isUpdating) {
				return;
			}

			// Calculate effective unit price before optimistic update (needed for rollback too)
			let effectiveUnitPrice = 0;
			if (itemIndex !== -1) {
				const currentQty = state.items[itemIndex].quantity;
				effectiveUnitPrice = (state.items[itemIndex].lineTotal > 0 && currentQty > 0)
					? state.items[itemIndex].lineTotal / currentQty
					: state.items[itemIndex].unitPrice;
			}

			// Optimistic update - update only the specific item
			if (itemIndex !== -1) {
				// Set updating flag to disable buttons
				state.items[itemIndex].isUpdating = true;

				// Check if item has custom pricing from add-ons (skip optimistic price for these)
				const hasCustomPricing = !!state.items[itemIndex].hasCustomPricing;

				// Update quantity immediately (always responsive)
				state.items[itemIndex].quantity = newQuantity;
				state.items[itemIndex].isAtMinQty = newQuantity <= (state.items[itemIndex].minQuantity || 1);
				state.items[itemIndex].isAtMaxQty = newQuantity >= (state.items[itemIndex].maxQuantity || Infinity);

				// Only update price optimistically for standard products (no add-ons)
				// Add-on products have rounding differences — wait for server response
				if (!hasCustomPricing) {
					const newLineTotal = effectiveUnitPrice * newQuantity;
					delete state.items[itemIndex].price;
					state.items[itemIndex].price = formatPriceSmart(newLineTotal);
					state.items[itemIndex].priceHtml = formatPrice(newLineTotal);
					state.items[itemIndex].lineTotal = newLineTotal;
				}

				// Update cart count immediately (always responsive)
				state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
				state.isItemSingular = state.cartCount === 1;
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);

				// Only update subtotal optimistically for standard products without coupons
				// Add-on products and coupon carts wait for server response to avoid price jumps
				const hasCoupons = state.coupons && state.coupons.length > 0;

				if (!hasCoupons && !hasCustomPricing) {
					const newSubtotal = state.items.reduce((total, item) => total + (item.lineTotal || 0), 0);
					state.cartSubtotal = newSubtotal;
					state.cartSubtotalFormatted = formatPrice(newSubtotal);
					state.cartTotal = formatPrice(newSubtotal);
					updateCartTotalsFromItems(state, true);
				}
			}

			// Sync with server in background
			try {
				await updateQuantityOnServer(cartKey, newQuantity, itemIndex);
			} catch (error) {
				// Rollback optimistic update on error
				if (itemIndex !== -1) {
					const rollbackLineTotal = effectiveUnitPrice * currentQuantity;

					state.items[itemIndex].quantity = currentQuantity;
					state.items[itemIndex].isAtMinQty = currentQuantity <= (state.items[itemIndex].minQuantity || 1);
					state.items[itemIndex].isAtMaxQty = currentQuantity >= (state.items[itemIndex].maxQuantity || Infinity);

					// Delete and re-assign price to force reactivity
					delete state.items[itemIndex].price;
					state.items[itemIndex].price = formatPriceSmart(rollbackLineTotal);
						state.items[itemIndex].priceHtml = formatPrice(rollbackLineTotal);

					state.items[itemIndex].lineTotal = rollbackLineTotal;

					// Recalculate totals
					state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
					state.isItemSingular = state.cartCount === 1;
					updateCartTotalsFromItems(state);
					updateCartEmptyClass(state.cartCount);
					updateCartWidgetCount(state.cartCount);
				}
			} finally {
				// Always clear the updating flag when done
				if (itemIndex !== -1) {
					state.items[itemIndex].isUpdating = false;
				}
			}
		},

		/**
		 * Decrease item quantity
		 */
		async decreaseQuantity() {
			const context = getContext();
			const cartKey = context.item.cartKey;
			const currentQuantity = context.item.quantity;
			const { state } = store('caddy/cart');

			// Block qty changes on fixed-qty bundled items
			const itemIndex = state.items.findIndex(item => item.cartKey === cartKey);
			if (itemIndex !== -1 && state.items[itemIndex].hideQuantityButtons) {
				return;
			}

			// Respect minimum quantity (defaults to 1)
			const minQty = context.item.minQuantity || 1;
			if (currentQuantity <= minQty) {
				return;
			}

			const newQuantity = currentQuantity - 1;

			// Prevent rapid clicks - check if this item is already updating
			if (itemIndex !== -1 && state.items[itemIndex].isUpdating) {
				return;
			}

			// Calculate effective unit price before optimistic update (needed for rollback too)
			let effectiveUnitPrice = 0;
			if (itemIndex !== -1) {
				const currentQty = state.items[itemIndex].quantity;
				effectiveUnitPrice = (state.items[itemIndex].lineTotal > 0 && currentQty > 0)
					? state.items[itemIndex].lineTotal / currentQty
					: state.items[itemIndex].unitPrice;
			}

			// Optimistic update - update only the specific item
			if (itemIndex !== -1) {
				// Set updating flag to disable buttons
				state.items[itemIndex].isUpdating = true;

				// Check if item has custom pricing from add-ons (skip optimistic price for these)
				const hasCustomPricing = !!state.items[itemIndex].hasCustomPricing;

				// Update quantity immediately (always responsive)
				state.items[itemIndex].quantity = newQuantity;
				state.items[itemIndex].isAtMinQty = newQuantity <= (state.items[itemIndex].minQuantity || 1);
				state.items[itemIndex].isAtMaxQty = newQuantity >= (state.items[itemIndex].maxQuantity || Infinity);

				// Only update price optimistically for standard products (no add-ons)
				// Add-on products have rounding differences — wait for server response
				if (!hasCustomPricing) {
					const newLineTotal = effectiveUnitPrice * newQuantity;
					delete state.items[itemIndex].price;
					state.items[itemIndex].price = formatPriceSmart(newLineTotal);
					state.items[itemIndex].priceHtml = formatPrice(newLineTotal);
					state.items[itemIndex].lineTotal = newLineTotal;
				}

				// Update cart count immediately (always responsive)
				state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
				state.isItemSingular = state.cartCount === 1;
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);

				// Only update subtotal optimistically for standard products without coupons
				// Add-on products and coupon carts wait for server response to avoid price jumps
				const hasCoupons = state.coupons && state.coupons.length > 0;

				if (!hasCoupons && !hasCustomPricing) {
					const newSubtotal = state.items.reduce((total, item) => total + (item.lineTotal || 0), 0);
					state.cartSubtotal = newSubtotal;
					state.cartSubtotalFormatted = formatPrice(newSubtotal);
					state.cartTotal = formatPrice(newSubtotal);
					updateCartTotalsFromItems(state, true);
				}
			}

			// Sync with server in background
			try {
				await updateQuantityOnServer(cartKey, newQuantity, itemIndex);
			} catch (error) {
				// Rollback optimistic update on error
				if (itemIndex !== -1) {
					const rollbackLineTotal = effectiveUnitPrice * currentQuantity;

					state.items[itemIndex].quantity = currentQuantity;
					state.items[itemIndex].isAtMinQty = currentQuantity <= (state.items[itemIndex].minQuantity || 1);
					state.items[itemIndex].isAtMaxQty = currentQuantity >= (state.items[itemIndex].maxQuantity || Infinity);

					// Delete and re-assign price to force reactivity
					delete state.items[itemIndex].price;
					state.items[itemIndex].price = formatPriceSmart(rollbackLineTotal);
						state.items[itemIndex].priceHtml = formatPrice(rollbackLineTotal);

					state.items[itemIndex].lineTotal = rollbackLineTotal;

					// Recalculate totals
					state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
					state.isItemSingular = state.cartCount === 1;
					updateCartTotalsFromItems(state);
					updateCartEmptyClass(state.cartCount);
					updateCartWidgetCount(state.cartCount);
				}
			} finally {
				// Always clear the updating flag when done
				if (itemIndex !== -1) {
					state.items[itemIndex].isUpdating = false;
				}
			}
		},

		/**
		 * Remove item from cart
		 */
		async removeItem() {
			const context = getContext();
			const cartKey = context.item.cartKey;
			const { state } = store('caddy/cart');

			const itemIndex = state.items.findIndex(item => item.cartKey === cartKey);
			if (itemIndex === -1) return;

			const removedItem = state.items[itemIndex];

			// Optimistic update
			state.items.splice(itemIndex, 1);

			// Bundle container: also remove its bundled children
			if (removedItem.isBundleContainer) {
				for (let i = state.items.length - 1; i >= 0; i--) {
					if (state.items[i].bundledBy === cartKey) {
						state.items.splice(i, 1);
					}
				}
			}

			state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
			state.isItemSingular = state.cartCount === 1;
			updateCartEmptyClass(state.cartCount);
			updateCartWidgetCount(state.cartCount);
			const rawSubtotal = state.items.reduce((total, item) => total + item.lineTotal, 0);
			state.cartSubtotal = Math.round(rawSubtotal * 100) / 100;
			updateCartTotalsFromItems(state, true);

			// Sync with server — serialize mutations to prevent 409 Conflict errors.
			// WooCommerce Store API rejects concurrent cart writes.
			pendingRemovals.add(cartKey);
			try {
				await queueCartMutation(() => removeItemFromServer(cartKey));
				pendingRemovals.delete(cartKey);
			} catch (error) {
				pendingRemovals.delete(cartKey);
				console.error('ERROR in removeItem:', error, {
					cartKey, itemName: removedItem.name
				});
				// Rollback by refreshing true server state
				await refreshCartFromServer();
			} finally {
				state.isLoading = false;
			}
		},

		/**
		 * Update cart totals
		 */
		updateTotals() {
			const { state } = store('caddy/cart');

			// Recalculate cart total
			const total = state.items.reduce((sum, item) => sum + item.lineTotal, 0);
			state.cartTotal = formatPrice(total);
			state.cartSubtotal = total;
		state.cartSubtotalFormatted = formatPrice(total);
			state.cartCount = state.items.reduce((total, item) => total + item.quantity, 0);
			state.isItemSingular = state.cartCount === 1;
		},

		/**
		 * Navigate to previous recommendation slide
		 */
		prevRecommendation() {
			const { state } = store('caddy/cart');
			if (state.recommendationIndex > 0) {
				state.recommendationIndex--;
			}
		},

		/**
		 * Navigate to next recommendation slide
		 */
		nextRecommendation() {
			const { state } = store('caddy/cart');
			const totalSlides = (state.recommendations && state.recommendations.length) || 0;
			if (state.recommendationIndex < totalSlides - 1) {
				state.recommendationIndex++;
			}
		},

		/**
		 * Handle drag/swipe start on recommendations slider
		 */
		onSliderPointerDown(event) {
			const { state } = store('caddy/cart');
			if (event.button && event.button !== 0) return;
			state._isDragging = false;
			state._dragStartX = event.clientX;
			state._dragOffset = 0;
			const slider = event.target.closest('.cc-pl-recommendations');
			if (slider) {
				slider.style.transition = 'none';
			}
		},

		onSliderPointerMove(event) {
			const { state } = store('caddy/cart');
			if (typeof state._dragStartX === 'undefined' || state._dragStartX === null) return;
			const diff = event.clientX - state._dragStartX;
			if (Math.abs(diff) > 5) {
				state._isDragging = true;
				state._dragOffset = diff;
			}
		},

		onSliderPointerUp(event) {
			const { state } = store('caddy/cart');
			if (!state._isDragging) {
				state._dragStartX = undefined;
				return;
			}
			const dragOffset = state._dragOffset || 0;
			state._dragOffset = 0;
			state._dragStartX = undefined;
			state._isDragging = false;
			const slider = event.target.closest('.cc-pl-recommendations');
			if (slider) {
				slider.style.transition = '';
			}
			const wrapper = event.target.closest('.cc-pl-upsells-wrapper');
			if (wrapper) {
				const blocker = (e) => {
					e.preventDefault();
					e.stopPropagation();
				};
				wrapper.addEventListener('click', blocker, { capture: true, once: true });
			}
			const totalSlides = (state.recommendations && state.recommendations.length) || 0;
			if (dragOffset < -50 && state.recommendationIndex < totalSlides - 1) {
				state.recommendationIndex++;
			} else if (dragOffset > 50 && state.recommendationIndex > 0) {
				state.recommendationIndex--;
			}
		},

		/**
		 * Add a recommendation product to cart
		 */
		async addRecommendationToCart() {
			const context = getContext();
			const product = context.rec;

			if (!product || !product.id) {
				return;
			}

			const { state } = store('caddy/cart');

			// Find the product in recommendations array and set loading state
			const recIndex = state.recommendations.findIndex(r => r.id === product.id);
			if (recIndex !== -1) {
				state.recommendations[recIndex].isAdding = true;
			}

			try {
				// Use WooCommerce Store API
				const response = await storeApiFetch('cart/add-item', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						id: parseInt(product.id),
						quantity: 1
					})
				});

				if (response.ok) {
					const cartData = await response.json();

					// Convert all items from Store API to Caddy format
					const caddyItems = convertCartItems(cartData.items);

					// Update cart state
					state.items = caddyItems;
					state.cartCount = cartData.items_count;
					state.isItemSingular = cartData.items_count === 1;
					updateCartTotals(state, cartData);
					updateCartEmptyClass(state.cartCount);
					updateCartWidgetCount(state.cartCount);
					state.coupons = cartData.coupons || [];
					state.discountTotal = getStoreApiDiscountTotal(state, cartData);

					// Update applied coupons display
					updateAppliedCouponsDisplay(cartData.coupons || []);

					// Trigger WooCommerce add to cart event
					window._caddyAddingToCart = true;
					document.dispatchEvent(new CustomEvent('wc_add_to_cart', {
						detail: { productId: product.id, quantity: 1 }
					}));
					window._caddyAddingToCart = false;

					// Refresh recommendations based on newly added product
					await refreshRecommendationsFromServer(state);
				}
			} catch (error) {
				console.error('Error adding recommendation to cart:', error);
			} finally {
				// Clear loading state
				if (recIndex !== -1 && state.recommendations[recIndex]) {
					state.recommendations[recIndex].isAdding = false;
				}
			}
		}

		// Save for Later actions (saveForLater, removeSavedItem, moveToCart) are added dynamically
		// by the SFL module when state.saveForLaterEnabled is true
	},

	// Callbacks for lifecycle events and debugging
	callbacks: {
		/**
		 * Update free shipping meter manually to avoid flash
		 */
		updateFreeShippingMeter() {
			const { state } = store('caddy/cart');

			// Only update if subtotal actually changed
			if (prevFreeShippingSubtotal === state.cartSubtotal) {
				return;
			}

			prevFreeShippingSubtotal = state.cartSubtotal;

			const amountEl = document.querySelector('.cc-fs-amount');
			if (amountEl) {
				const remaining = calculateFreeShippingRemainingFormatted();
				amountEl.innerHTML = remaining;
			}

			const meterBar = document.querySelector('.cc-fs-meter-used');
			if (meterBar) {
				const percentage = calculateFreeShippingPercentage();
				meterBar.style.width = percentage;

				const isAchieved = calculateFreeShippingAchieved();
				if (isAchieved) {
					meterBar.classList.add('cc-bar-active');
				} else {
					meterBar.classList.remove('cc-bar-active');
				}
			}
		},

		/**
		 * Clean up server-rendered items after Interactivity API template finishes rendering
		 * Called via data-wp-watch directive - runs whenever state changes
		 */
		cleanupServerRendered() {
			const { state } = store('caddy/cart');

			// Only cleanup if we have items in state (Interactivity API has data)
			if (!state.items || state.items.length === 0) {
				return;
			}

			// Check if Interactivity API has rendered items (look for items WITHOUT cc-ssr-item class)
			const interactiveItems = document.querySelectorAll('.cc-cart-item:not(.cc-ssr-item)');

			// Only remove server-rendered items if interactive items are present
			if (interactiveItems.length > 0) {
				const serverRenderedItems = document.querySelectorAll('.cc-ssr-item');
				serverRenderedItems.forEach(item => {
					item.remove();
				});
			}
		},

		/**
		 * Initialize cart when DOM is ready
		 */
		async init() {
			const { state } = store('caddy/cart');
			const context = getContext();
			ensureI18nState();

			// Schedule background prefetch of recommendations (non-blocking)
			scheduleRecommendationsPrefetch();

			// Auto-open cart if configured and items were just added
			if (context && context.autoOpen && state.cartCount > 0) {
				// Check if this is likely a fresh add-to-cart action
				const urlParams = new URLSearchParams(window.location.search);
				if (urlParams.get('add-to-cart')) {
					setTimeout(() => {
						store('caddy/cart').actions.openCart();
					}, 100);
				}
			}

			// Listen for WooCommerce cart updates
			document.addEventListener('wc_cart_emptied', () => {
				state.items = [];
				state.cartCount = 0;
				state.cartTotal = formatPrice(0);
				state.cartSubtotal = 0;
			state.cartSubtotalFormatted = formatPrice(0);
				updateCartWidgetCount(state.cartCount);
			});

			// Global click handler for [cc_cart_items] and [cc_saved_items] shortcode widgets
			document.addEventListener('click', (e) => {
				const cartWidget = e.target.closest('.cc_cart_items_list');
				if (cartWidget) {
					e.preventDefault();
					store('caddy/cart').actions.openCart();
					return;
				}
				const savedWidget = e.target.closest('.cc_saved_items_list');
				if (savedWidget) {
					e.preventDefault();
					store('caddy/cart').actions.openCart('saves');
					return;
				}
			});

			// Listen for add to cart events
			document.addEventListener('wc_add_to_cart', async () => {
				// Skip if this is from moveToCart or our own handlers (they manage their own updates)
				if (window._caddyMovingToCart || window._caddyAddingToCart) {
					return;
				}

				try {
					// Refresh cart from WooCommerce Store API
					await refreshCartFromServer();

					// Update recommendations with new cart items (only if cart has items)
					const { state } = store('caddy/cart');
					if (state.items && state.items.length > 0) {
						initializeRecommendations();
					}
				} catch (error) {
					console.warn('Caddy: cart operation failed:', error);
				}
			});

			// Handle escape key
			document.addEventListener('keydown', (event) => {
				if (event.key === 'Escape' && state.isOpen) {
					store('caddy/cart').actions.closeCart();
				}
			});

			// Intercept add-to-cart forms to prevent page refresh (single product and shop pages)
			// Match: form.cart, variations_form class, or any form with add-to-cart/product_id inputs
			const addToCartForms = findAddToCartForms('form.cart, form.variations_form, .woocommerce form[action*="add-to-cart"]');
			const addToCartButtons = document.querySelectorAll('a.add_to_cart_button, .wc-block-grid__product-add-to-cart a');

			// Handle traditional forms (single product pages) - use capture phase and high priority
			addToCartForms.forEach(form => {
				// Mark form as handled by Interactivity API to prevent duplicate DOMContentLoaded handler
				form.dataset.caddyIntercepted = 'true';

				// Remove any existing onsubmit handlers
				form.onsubmit = null;

				// Override the submit method to prevent any programmatic submission
				const originalSubmit = form.submit;
				form.submit = function() {
					return false;
				};

				// Use capture phase to intercept before other handlers
				form.addEventListener('submit', async (event) => {
					event.preventDefault();
					event.stopImmediatePropagation();

					const formData = new FormData(form);
					// Submit button values aren't included in FormData, so fall back to the button's value attribute
					const addToCartBtn = form.querySelector('button[name="add-to-cart"], input[name="add-to-cart"]');
					const productId = formData.get('add-to-cart') || formData.get('product_id') || (addToCartBtn && addToCartBtn.value);
					const quantity = parseInt(formData.get('quantity')) || 1;
					const variation = formData.get('variation_id') || '';

					// Add loading state
					state.isLoading = true;
					const submitBtn = form.querySelector('[type="submit"]');
					const originalText = submitBtn ? submitBtn.textContent : '';
					if (submitBtn) {
						submitBtn.textContent = state.i18n.adding;
						submitBtn.disabled = true;
					}

					try {
						const isBundle = form.classList.contains('bundle_form');
						const variationInt = parseInt(variation) || 0;

						// Detect if form has custom fields that Store API can't handle
						// Standard fields are: add-to-cart, product_id, quantity, variation_id, attribute_*, bundled_*, bundle_*
						const standardFieldPattern = /^(add-to-cart|product_id|quantity(\[.*\])?|variation_id|attribute_.*|bundled_.*|bundle_.*|wc-bundle-quantity_.*|stamp\[.*|_wpnonce|_wp_http_referer)$/;
						let hasCustomFields = false;
						for (const [key] of formData.entries()) {
							if (!standardFieldPattern.test(key)) {
								hasCustomFields = true;
								break;
							}
						}

						// Let product-type handlers contribute to API data
						const matchedHandlers = handlers.filter(h => h.matches(form, formData));

						if (hasCustomFields) {
							// HYBRID PATH: Product has custom fields (gift cards, add-ons, etc.)
							// Use traditional POST to add item (all plugin hooks fire), then refresh cart from Store API
							const postData = new URLSearchParams();
							for (const [key, value] of formData.entries()) {
								postData.append(key, value);
							}
							// Ensure add-to-cart field is present
							if (!formData.get('add-to-cart') && productId) {
								postData.append('add-to-cart', productId);
							}

							const postResponse = await fetch(window.location.href, {
								method: 'POST',
								credentials: 'same-origin',
								headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
								body: postData.toString(),
								redirect: 'manual'
							});

							// Traditional POST returns a redirect on success (302) or the page with errors
							// redirect: 'manual' gives us an opaque redirect response (type: 'opaqueredirect', status: 0)
							if (postResponse.type === 'opaqueredirect' || postResponse.ok || postResponse.status === 0) {
								// Item was added — refresh cart state from Store API
								await refreshCartFromServer();
								const cartStore = store('caddy/cart');

								// Trigger WooCommerce add to cart event for other plugins
								window._caddyAddingToCart = true;
								document.dispatchEvent(new CustomEvent('wc_add_to_cart', {
									detail: { productId, quantity, variation }
								}));
								window._caddyAddingToCart = false;

								// Auto-open cart after successful add
								setTimeout(() => {
									store('caddy/cart').actions.openCart('cart', true);
								}, 100);

								// Show success feedback
								if (submitBtn) {
									submitBtn.textContent = state.i18n.added;
									setTimeout(() => {
										if (submitBtn) submitBtn.textContent = originalText;
									}, 1500);
								}
							} else {
								throw new Error(state.i18n.errorTryAgain);
							}
						} else {
							// STORE API PATH: Standard product types (simple, variable, grouped, bundles)
							const addToCartData = {
								id: (variationInt > 0 && !isBundle) ? variationInt : parseInt(productId),
								quantity: parseInt(quantity)
							};

							// Variable product attributes
							if (variationInt > 0) {
								const attributes = [];
								for (const [key, value] of formData.entries()) {
									if (key.startsWith('attribute_')) {
										attributes.push({
											attribute: key.replace('attribute_', ''),
											value: value
										});
									}
								}
								if (attributes.length > 0) {
									addToCartData.variation = attributes;
								}
							}

							for (const handler of matchedHandlers) {
								const extra = handler.buildApiData(form, formData, productId);
								if (extra) Object.assign(addToCartData, extra);
							}

							const response = await storeApiFetch('cart/add-item', {
								method: 'POST',
								headers: {
									'Content-Type': 'application/json',
								},
								body: JSON.stringify(addToCartData)
							});

							if (response.ok) {
								// Store API add-item already returns the full cart - no need for second request!
								const cartData = await response.json();
								const cartStore = store('caddy/cart');

								// Convert Store API format to Caddy format
								const caddyItems = convertCartItems(cartData.items);

								cartStore.state.items = caddyItems;
								cartStore.state.cartCount = cartData.items_count;
								cartStore.state.isItemSingular = cartData.items_count === 1;
								updateCartTotals(cartStore.state, cartData);
								updateCartEmptyClass(cartStore.state.cartCount);
								updateCartWidgetCount(cartStore.state.cartCount);
								cartStore.state.cartHash = cartData.cartHash || 'updated';
								cartStore.state.coupons = cartData.coupons || [];
								cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

								// Update applied coupons display to refresh discount amount
								updateAppliedCouponsDisplay(cartData.coupons || []);

								// Trigger WooCommerce add to cart event for other plugins
								window._caddyAddingToCart = true;
								document.dispatchEvent(new CustomEvent('wc_add_to_cart', {
									detail: { productId, quantity, variation }
								}));
								window._caddyAddingToCart = false;

								// Run post-add handlers (bundle-sells, etc.)
								for (const handler of matchedHandlers) {
									if (handler.afterAddToCart) {
										await handler.afterAddToCart(form, formData, cartData, {
											refreshCartFromServer, initializeRecommendations
										});
									}
								}

								// Auto-open cart after successful add
								setTimeout(() => {
									store('caddy/cart').actions.openCart('cart', true);
								}, 100);

								// Show success feedback
								if (submitBtn) {
									submitBtn.textContent = state.i18n.added;
									setTimeout(() => {
										if (submitBtn) submitBtn.textContent = originalText;
									}, 1500);
								}
							} else {
								let errorMsg = '';
								try {
									const errorJson = await response.json();
									errorMsg = errorJson.message || '';
								} catch (e) {
									errorMsg = '';
								}
								throw new Error(errorMsg || `Store API returned status: ${response.status}`);
							}
						}
					} catch (error) {
						const errorMsg = error.message || state.i18n.errorTryAgain;
						const noticesWrapper = form.closest('.product')?.querySelector('.woocommerce-notices-wrapper')
							|| document.querySelector('.woocommerce-notices-wrapper');
						if (noticesWrapper) {
							while (noticesWrapper.firstChild) noticesWrapper.removeChild(noticesWrapper.firstChild);
							const notice = document.createElement('ul');
							notice.className = 'woocommerce-error';
							notice.setAttribute('role', 'alert');
							const li = document.createElement('li');
							li.textContent = errorMsg.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#039;/g, "'");
							notice.appendChild(li);
							noticesWrapper.appendChild(notice);
							noticesWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
						}
						if (submitBtn) {
							submitBtn.textContent = originalText;
						}
					} finally {
						// Reset button state
						state.isLoading = false;
						if (submitBtn) {
							submitBtn.disabled = false;
						}
					}

					// Always prevent form submission
					return false;
				}, true); // Use capture phase
			});

			// Handle shop page add-to-cart buttons
			addToCartButtons.forEach(button => {
				// Mark button as handled by Interactivity API to prevent duplicate DOMContentLoaded handler
				button.dataset.caddyIntercepted = 'true';

				button.addEventListener('click', async (event) => {
					// Skip variable and grouped product buttons - let them navigate to product page
					if (button.classList.contains('product_type_variable') ||
					    button.classList.contains('product_type_grouped')) {
						return; // Don't prevent default, allow normal navigation
					}

					// Skip "Read more" buttons (non-purchasable products)
					if (!button.classList.contains('add_to_cart_button')) {
						return; // Don't prevent default, allow normal navigation
					}

					// Skip bundles that require configuration and external products —
					// they can't be added via the Store API; let them navigate
					if (button.classList.contains('product_type_bundle_input_required') ||
					    button.classList.contains('product_type_external')) {
						return;
					}

					event.preventDefault();
					event.stopPropagation();

					const productId = button.dataset.product_id || button.getAttribute('data-product_id');
					const quantity = button.dataset.quantity || 1;

					if (!productId) {
						return;
					}


					// Add loading state — use CSS classes only to preserve theme icons/markup
					state.isLoading = true;
					button.classList.add('loading');
					button.disabled = true;

					try {
						// Use Store API add-item for consistent, fast performance
						const response = await storeApiFetch('cart/add-item', {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify({
								id: productId,
								quantity: parseInt(quantity)
							})
						});

						if (response.ok) {
							// Store API add-item already returns the full cart - use it!
							const cartData = await response.json();
							const cartStore = store('caddy/cart');

							// Convert Store API format to Caddy format
							const caddyItems = convertCartItems(cartData.items);

							cartStore.state.items = caddyItems;
							cartStore.state.cartCount = cartData.items_count;
							cartStore.state.isItemSingular = cartData.items_count === 1;
							updateCartTotals(cartStore.state, cartData);
							updateCartEmptyClass(cartStore.state.cartCount);
							updateCartWidgetCount(cartStore.state.cartCount);
							cartStore.state.cartHash = cartData.cartHash || 'updated';
							cartStore.state.coupons = cartData.coupons || [];
							cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

							// Update applied coupons display
							updateAppliedCouponsDisplay(cartData.coupons || []);

							// Trigger WooCommerce add to cart event for compatibility
							window._caddyAddingToCart = true;
							document.dispatchEvent(new CustomEvent('wc_add_to_cart', {
								detail: { productId, quantity }
							}));
							window._caddyAddingToCart = false;

							// Auto-open cart after successful add (skip fetch - we already have fresh data!)
							setTimeout(() => {
								store('caddy/cart').actions.openCart('cart', true);
							}, 100);

							// Show success via CSS class — preserves theme icons/markup
							button.classList.remove('loading');
							button.classList.add('added');
							setTimeout(() => {
								button.classList.remove('added');
							}, 1500);
						} else {
							const { state: cartState } = store('caddy/cart');
							throw new Error(cartState.i18n.errorAddToCartFailed);
						}
					} catch (error) {
						// Fallback to original link behavior
						window.location.href = button.href;
					} finally {
						// Reset button state
						state.isLoading = false;
						button.classList.remove('loading');
						button.disabled = false;
					}
				});
			});
		},

	}
});


// Simple, direct form and button interception for single product and shop pages
document.addEventListener('DOMContentLoaded', function() {
	// Handle single product page forms - match all WooCommerce add-to-cart forms
	const forms = findAddToCartForms('form.cart, form.variations_form');

	forms.forEach(form => {
		form.addEventListener('submit', async function(e) {
			// Skip if already handled by the Interactivity API init callback
			if (form.dataset.caddyIntercepted === 'true') {
				return;
			}

			e.preventDefault();

			const formData = new FormData(form);
			// Get product ID from the submit button's value attribute
			const productId = form.querySelector('button[name="add-to-cart"]')?.value ||
							  formData.get('add-to-cart') ||
							  formData.get('product_id') ||
							  form.querySelector('input[name="add-to-cart"]')?.value;
			const quantity = parseInt(formData.get('quantity')) || 1;
			const variationId = formData.get('variation_id') || 0;


			const submitBtn = form.querySelector('[type="submit"]');
			const originalText = submitBtn?.textContent || '';
			const { state } = store('caddy/cart');

			if (submitBtn) {
				submitBtn.textContent = state.i18n.adding;
				submitBtn.disabled = true;
			}

			// Use WooCommerce's native cart add method
			try {
				// Pass all form data to ensure custom fields (gift cards, add-ons, etc.) are included
				const addData = new URLSearchParams();
				for (const [key, value] of formData.entries()) {
					addData.append(key, value);
				}
				// Ensure add-to-cart field is present
				if (!formData.get('add-to-cart') && productId) {
					addData.append('add-to-cart', productId);
				}

				const response = await fetch(window.location.href, {
					method: 'POST',
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: addData.toString(),
					redirect: 'manual'
				});

				if (response.type === 'opaqueredirect' || response.ok || response.status === 0) {
					// Refresh cart from WooCommerce Store API
					await refreshCartFromServer();

					// Open cart (skip fetch - we just refreshed!)
					store('caddy/cart').actions.openCart('cart', true);

					if (submitBtn) {
						const { state } = store('caddy/cart');
						submitBtn.textContent = state.i18n.added;
						setTimeout(() => { if (submitBtn) submitBtn.textContent = originalText; }, 1500);
					}
				} else {
					const { state } = store('caddy/cart');
					throw new Error(state.i18n.errorAddToCartFailed);
				}
			} catch (error) {
				if (submitBtn) {
					const { state } = store('caddy/cart');
					submitBtn.textContent = state.i18n.error;
					setTimeout(() => { if (submitBtn) submitBtn.textContent = originalText; }, 2000);
				}
			} finally {
				if (submitBtn) {
					submitBtn.disabled = false;
				}
			}
		});
	});

	// Handle shop page add-to-cart buttons
	const shopButtons = document.querySelectorAll('a.add_to_cart_button, .wc-block-grid__product-add-to-cart a, .woocommerce ul.products li.product .button[data-product_id]');

	shopButtons.forEach(button => {
		button.addEventListener('click', async function(e) {
			// Skip if already handled by the Interactivity API init callback
			if (this.dataset.caddyIntercepted === 'true') {
				return;
			}

			// Skip variable and grouped product buttons - let them navigate to product page
			if (this.classList.contains('product_type_variable') ||
			    this.classList.contains('product_type_grouped')) {
				return; // Don't prevent default, allow normal navigation
			}

			// Skip "Read more" buttons (non-purchasable products)
			if (!this.classList.contains('add_to_cart_button')) {
				return; // Don't prevent default, allow normal navigation
			}

			// Skip bundles that require configuration and external products —
			// they can't be added via the Store API; let them navigate
			if (this.classList.contains('product_type_bundle_input_required') ||
			    this.classList.contains('product_type_external')) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			const productId = this.dataset.product_id || this.getAttribute('data-product_id');
			const quantity = parseInt(this.dataset.quantity) || 1;

			if (!productId) {
				return;
			}


			// Use CSS classes only to preserve theme icons/markup
			this.disabled = true;
			this.classList.add('loading');

			try {
				// Use WooCommerce Store API as per OPTIMIZATION_PLAN.md requirements

				const addToCartData = {
					id: parseInt(productId),
					quantity: parseInt(quantity)
				};

				const response = await storeApiFetch('cart/add-item', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(addToCartData)
				});


				if (response.ok) {
					// Store API add-item already returns the full cart - use it!
					const cartData = await response.json();
					const cartStore = store('caddy/cart');

					// Convert Store API format to Caddy format
					const caddyItems = convertCartItems(cartData.items);

					cartStore.state.items = caddyItems;
					cartStore.state.cartCount = cartData.items_count;
					cartStore.state.isItemSingular = cartData.items_count === 1;
					updateCartTotals(cartStore.state, cartData);
					updateCartEmptyClass(cartStore.state.cartCount);
					updateCartWidgetCount(cartStore.state.cartCount);
					cartStore.state.coupons = cartData.coupons || [];
					cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

					// Update applied coupons display to refresh discount amount
					updateAppliedCouponsDisplay(cartData.coupons || []);

					// Auto-open cart after successful add (skip fetch - we already have fresh data!)
					store('caddy/cart').actions.openCart('cart', true);

					// Show success via CSS class — preserves theme icons/markup
					this.classList.remove('loading');
					this.classList.add('added');
					setTimeout(() => { this.classList.remove('added'); }, 1500);
				} else {
					const errorResponse = await response.text();
					throw new Error(`Store API returned status: ${response.status} - ${errorResponse}`);
				}
			} catch (error) {
				// Silent fail — button returns to normal state
				console.warn('Caddy: cart operation failed:', error);
			} finally {
				this.disabled = false;
				this.classList.remove('loading');
			}
		});
	});
});

/**
 * Update item quantity on server
 *
 * @param {string} cartKey Cart item key
 * @param {number} quantity New quantity
 * @returns {Promise}
 */
async function updateQuantityOnServer(cartKey, quantity, itemIndex) {
	// Use WooCommerce Store API for cart item updates
	const requestData = {
		quantity: parseInt(quantity)
	};

	const response = await storeApiFetch(`cart/items/${cartKey}`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(requestData)
	});

	if (!response.ok) {
		const errorText = await response.text();
		const { state } = store('caddy/cart');
		throw new Error(state.i18n.errorUpdateFailed);
	}

	// PUT returns the updated item with correct quantity and pricing
	const updatedItem = await response.json();

	// Update state - just update the specific fields, no need to replace entire item!
	const cartStore = store('caddy/cart');

	// Find the item in state
	const updatedItemIndex = cartStore.state.items.findIndex(item => item.cartKey === updatedItem.key);
	if (updatedItemIndex !== -1) {
		const stateItem = cartStore.state.items[updatedItemIndex];

		// Only update the fields that changed: quantity and pricing
		stateItem.quantity = updatedItem.quantity;

		// Use line_subtotal (undiscounted) for item display, not line_total (discounted)
		// Cart-level discounts should only affect cart totals, not individual item prices
		// Store API totals are always ex-tax; include tax when cart display is tax-inclusive
		let itemTotal = convertStoreApiPrice(updatedItem.totals.line_subtotal);
		if (cartStore.state.taxDisplayCart === 'incl') {
			itemTotal += convertStoreApiPrice(updatedItem.totals.line_subtotal_tax || 0);
		}
		stateItem.lineTotal = itemTotal;

		// Delete and re-assign price to force reactivity
		delete stateItem.price;
		stateItem.price = formatPriceSmart(itemTotal);
		stateItem.priceHtml = formatPrice(itemTotal);

		// Recalculate cart count from all items
		cartStore.state.cartCount = cartStore.state.items.reduce((total, item) => total + item.quantity, 0);
		cartStore.state.isItemSingular = cartStore.state.cartCount === 1;

		// Recalculate totals from items (handles coupons via existing logic)
		updateCartTotalsFromItems(cartStore.state);

		// Update empty class and widget
		updateCartEmptyClass(cartStore.state.cartCount);
		updateCartWidgetCount(cartStore.state.cartCount);
	}

	// Dispatch cart update event for premium features (rewards meter, etc.)
	document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { item: updatedItem } }));

	// Broadcast update to other tabs
	broadcastCartUpdate();

	return { success: true };
}

/**
 * Remove item from server
 *
 * @param {string} cartKey Cart item key
 * @returns {Promise}
 */
async function removeItemFromServer(cartKey) {
	// Use WooCommerce Store API POST remove-item endpoint (returns full cart data,
	// unlike DELETE /cart/items/{key} which returns 204 No Content and prevents
	// the JS from learning about server-side changes like bundle child/coupon removal)
	const response = await storeApiFetch('cart/remove-item', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		keepalive: true,
		body: JSON.stringify({ key: cartKey })
	});

	if (!response.ok) {
		let errorData;
		try {
			errorData = await response.json();
		} catch (e) {
			errorData = await response.text();
		}

		console.error('Failed to remove cart item:', {
			status: response.status,
			statusText: response.statusText,
			cartKey: cartKey,
			url: getStoreApiUrl('cart/remove-item'),
			error: errorData,
			currentCartState: store('caddy/cart').state.items.map(i => ({ key: i.cartKey, name: i.name }))
		});

		const { state } = store('caddy/cart');
		throw new Error(state.i18n.errorRemoveFailed);
	}

	// Check if response has content before parsing
	const contentType = response.headers.get('content-type');
	let cartData = null;

	// Only try to parse JSON if there's content
	if (contentType && contentType.includes('application/json')) {
		const text = await response.text();
		if (text && text.trim().length > 0) {
			try {
				cartData = JSON.parse(text);
			} catch (e) {
				console.warn('Remove-item response is not valid JSON, skipping cart data update:', text);
			}
		}
	}

	if (cartData) {
		const cartStore = store('caddy/cart');

		// Convert Store API format to Caddy format
		const serverItems = convertCartItems(cartData.items);

		// Filter out items that are still being removed by concurrent requests
		// (prevents rapid-removal race condition where server response re-adds items)
		const filteredItems = pendingRemovals.size > 0
			? serverItems.filter(item => !pendingRemovals.has(item.cartKey))
			: serverItems;

		// Don't replace the entire items array — the optimistic removal already
		// removed the item from state. Only make surgical changes if the server
		// response shows unexpected differences (e.g., bundle children removed).
		const currentKeys = new Set(cartStore.state.items.map(item => item.cartKey));
		const serverKeys = new Set(filteredItems.map(item => item.cartKey));

		// Remove items that server no longer has (beyond the optimistic removal)
		for (let i = cartStore.state.items.length - 1; i >= 0; i--) {
			if (!serverKeys.has(cartStore.state.items[i].cartKey)) {
				cartStore.state.items.splice(i, 1);
			}
		}

		// Never re-add items during a remove response — stale server sessions
		// can return already-removed items. Only refreshCartFromServer() adds items.

		cartStore.state.cartCount = cartStore.state.items.reduce((total, item) => total + item.quantity, 0);
		updateCartEmptyClass(cartStore.state.cartCount);
		updateCartWidgetCount(cartStore.state.cartCount);

		// Recalculate totals from LOCAL items (not server response which may include
		// stale items still pending removal on server side)
		updateCartTotalsFromItems(cartStore.state);

		cartStore.state.cartHash = cartData.cartHash || 'updated';
		cartStore.state.coupons = cartData.coupons || [];
		cartStore.state.discountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

		// Update widget counts
		updateCartWidgetCount(cartStore.state.cartCount);

		// Update applied coupons display to refresh discount amount
		updateAppliedCouponsDisplay(cartData.coupons || []);

		// Dispatch cart update event for premium features (rewards meter, etc.)
		document.dispatchEvent(new CustomEvent('caddy_cart_updated', { detail: { cartData } }));

		// Refresh recommendations based on remaining cart items
		initializeRecommendations();
	} else {
		// No cart data in response - optimistic update succeeded
		// Still refresh recommendations
		initializeRecommendations();
	}

	// Broadcast update to other tabs
	broadcastCartUpdate();

	return { success: true };
}

/**
 * Calculate original total (before sale discounts) from cart items
 *
 * @param {Array} items Cart items array
 * @returns {number} Original total in dollars
 */
function calculateOriginalTotal(items) {
	if (!items || items.length === 0) return 0;
	return items.reduce((total, item) => {
		const regularPrice = item.regularPrice || 0;
		const quantity = item.quantity || 0;
		return total + (regularPrice * quantity);
	}, 0);
}

function getStoreApiDiscountTotal(state, cartData) {
	let couponDiscount = convertStoreApiPrice(cartData?.totals?.total_discount || 0);
	if (state?.taxDisplayCart === 'incl') {
		couponDiscount += convertStoreApiPrice(cartData?.totals?.total_discount_tax || 0);
	}
	return couponDiscount;
}

function getStoreApiSubtotal(state, cartData) {
	let itemsTotal = convertStoreApiPrice(cartData?.totals?.total_items || 0);
	if (state?.taxDisplayCart === 'incl') {
		itemsTotal += convertStoreApiPrice(cartData?.totals?.total_items_tax || 0);
	}
	return itemsTotal - getStoreApiDiscountTotal(state, cartData);
}

/**
 * Update all cart totals including subtotal, original total, and discount flag
 * Call this function whenever the cart is updated to ensure all prices are synchronized
 *
 * @param {Object} state Cart state object
 * @param {Object} cartData WooCommerce Store API cart data
 */
function updateCartTotals(state, cartData) {
	// Store API totals are ex-tax; include tax totals when cart display is inclusive.
	state.cartSubtotal = getStoreApiSubtotal(state, cartData);
	state.cartSubtotalDisplay = formatPriceSmart(state.cartSubtotal);
	state.cartSubtotalFormatted = formatPrice(state.cartSubtotal);

	// Calculate original total (before sale discounts)
	state.originalTotal = calculateOriginalTotal(state.items);
	state.originalTotalDisplay = formatPriceSmart(state.originalTotal);
	state.originalTotalFormatted = formatPrice(state.originalTotal);

	// Set discount flag (only show crossed-out price if there's an actual discount)
	state.hasDiscount = state.originalTotal > state.cartSubtotal;

	// Update grand total
	state.cartTotal = cartData.totals.total_formatted || formatPrice(convertStoreApiPrice(cartData.totals.total_price));
}

/**
 * Update cart totals after optimistic client-side changes (rollbacks, etc)
 * Use this when you don't have server data but need to recalculate from items
 *
 * @param {Object} state Cart state object
 * @param {boolean} skipSubtotal If true, skips subtotal recalculation (use when subtotal already set)
 */
function updateCartTotalsFromItems(state, skipSubtotal = false) {
	// Calculate subtotal from items (unless already calculated)
	if (!skipSubtotal) {
		state.cartSubtotal = state.items.reduce((total, item) => total + item.lineTotal, 0);
		state.cartSubtotalDisplay = formatPriceSmart(state.cartSubtotal);
		state.cartSubtotalFormatted = formatPrice(state.cartSubtotal);
		state.cartTotal = formatPrice(state.cartSubtotal);
	} else {
		// Even when skipping subtotal calc, update formatted values to match current subtotal
		state.cartSubtotalDisplay = formatPriceSmart(state.cartSubtotal);
		state.cartSubtotalFormatted = formatPrice(state.cartSubtotal);
		state.cartTotal = formatPrice(state.cartSubtotal);
	}

	// Calculate original total (before sale discounts)
	state.originalTotal = calculateOriginalTotal(state.items);
	state.originalTotalDisplay = formatPriceSmart(state.originalTotal);
	state.originalTotalFormatted = formatPrice(state.originalTotal);

	// Set discount flag (only show crossed-out price if there's an actual discount)
	state.hasDiscount = state.originalTotal > state.cartSubtotal;
}

/**
 * Set up cross-tab synchronization
 */
function setupCrossTabSync() {

	// Option 1: Use BroadcastChannel if available (best performance)
	if ('BroadcastChannel' in window) {
		const channel = new BroadcastChannel('caddy_cart_sync');

		// Listen for updates from other tabs
		channel.addEventListener('message', async (event) => {

			if (event.data.type === 'cart_updated') {
				// Fetch fresh cart state from server
				await refreshCartFromServer();
			}
		});

		// Store reference to channel for broadcasting
		window.caddyCartChannel = channel;
	}
	// Option 2: Fallback to localStorage events
	else {
		window.addEventListener('storage', async (event) => {
			if (event.key === 'caddy_cart_update') {
				// Fetch fresh cart state from server
				await refreshCartFromServer();
			}
		});
	}

	// Also poll for updates periodically when tab becomes visible
	document.addEventListener('visibilitychange', async () => {
		if (!document.hidden) {
			await refreshCartFromServer();
		}
	});

	// Flush any pending removals before page unload so they aren't lost.
	// Fires all at once with keepalive — 409s don't matter since we're leaving.
	window.addEventListener('beforeunload', () => {
		if (pendingRemovals.size === 0) return;
		// Plain fetch with keepalive — no retry logic possible during unload
		const nonce = getStoreApiNonce();
		const headers = { 'Content-Type': 'application/json' };
		if (nonce) headers['Nonce'] = nonce;

		const storeApiUrl = getStoreApiUrl('cart/remove-item');
		for (const cartKey of pendingRemovals) {
			fetch(storeApiUrl, {
				method: 'POST',
				headers,
				credentials: 'same-origin',
				keepalive: true,
				body: JSON.stringify({ key: cartKey })
			});
		}
	});
}

/**
 * Broadcast cart update to other tabs
 */
function broadcastCartUpdate() {

	// Use BroadcastChannel if available
	if (window.caddyCartChannel) {
		window.caddyCartChannel.postMessage({
			type: 'cart_updated',
			timestamp: Date.now()
		});
	}
	// Fallback to localStorage
	else {
		localStorage.setItem('caddy_cart_update', JSON.stringify({
			timestamp: Date.now()
		}));
		// Clean up after a moment
		setTimeout(() => {
			localStorage.removeItem('caddy_cart_update');
		}, 100);
	}
}

// Promise of the in-flight refresh (null when idle) — lets overlapping callers
// await a completed read instead of getting a silent no-op.
let refreshInFlight = null;
// Flag to request a follow-up pass if a refresh was asked for while one was running
let refreshQueued = false;

// Track cart keys being removed to prevent refresh from re-adding them
const pendingRemovals = new Set();

// Serialize server-side cart mutations to prevent 409 Conflict errors.
// WooCommerce Store API locks the cart during writes — concurrent POSTs get rejected.
// Optimistic UI updates are still instant; only the server calls are queued.
let cartMutationChain = Promise.resolve();

function queueCartMutation(fn) {
	const result = cartMutationChain.then(fn, fn);
	cartMutationChain = result.catch(() => {}); // prevent unhandled rejection from breaking chain
	return result;
}

/**
 * Update the cart empty class based on cart count
 * @param {number} cartCount - Current number of items in cart
 */
function updateCartEmptyClass(cartCount) {
	const cartBody = document.querySelector('.cc-cart .cc-body');
	if (!cartBody) return;

	if (cartCount === 0) {
		cartBody.classList.add('cc-empty');
	} else {
		cartBody.classList.remove('cc-empty');
	}
}

/**
 * Update cart widget count
 *
 * @param {number} count Cart items count
 */
/**
 * Switch between cart tabs and update header title/icons
 */
function switchToTab(tabId) {
	// Remove active classes from all tabs
	document.querySelectorAll('.cc-screen-tab').forEach(tab => {
		tab.classList.remove('cc-active');
		tab.classList.remove('cc-tab-active');
	});

	// Remove active state from all tab links
	document.querySelectorAll('[data-tabs] a').forEach(link => {
		link.classList.remove('active');
		link.setAttribute('aria-selected', 'false');
	});

	// Add active class to target tab
	const targetTab = document.querySelector(tabId);
	if (targetTab) {
		if (tabId === '#cc-cart') {
			targetTab.classList.add('cc-active');
		} else {
			targetTab.classList.add('cc-tab-active');
		}
	}

	// Add active state to corresponding link
	const targetLink = document.querySelector(`[data-tabs] a[href="${tabId}"]`);
	if (targetLink) {
		targetLink.classList.add('active');
		targetLink.setAttribute('aria-selected', 'true');
	}

	// Update header title and back button icon based on active tab
	const headerTitle = document.querySelector('.cc-header-title');
	const closeIcon = document.querySelector('.cc-header-back-close');
	const arrowIcon = document.querySelector('.cc-header-back-arrow');

	if (headerTitle) {
		if (!headerTitle._originalContent) {
			headerTitle._originalContent = headerTitle.cloneNode(true);
		}
		if (tabId === '#cc-cart') {
			headerTitle.replaceChildren(...headerTitle._originalContent.cloneNode(true).childNodes);
		} else {
			const { state: i18nState } = store('caddy/cart');
			const titles = {
				'#cc-saves': i18nState?.i18n?.yourSaves || 'Your Saves',
				'#cc-offers': i18nState?.i18n?.yourOffers || 'Your Offers',
			};
			headerTitle.textContent = titles[tabId] || i18nState?.i18n?.yourCart || 'Your Cart';
		}
	}

	if (closeIcon && arrowIcon) {
		if (tabId === '#cc-cart') {
			closeIcon.style.display = '';
			arrowIcon.style.display = 'none';
		} else {
			closeIcon.style.display = 'none';
			arrowIcon.style.display = '';
		}
	}
}

function updateCartWidgetCount(count) {
	const cartCountElements = document.querySelectorAll('.cc_cart_count');
	cartCountElements.forEach(element => {
		element.textContent = count;

		// Update classes for zero state
		if (count === 0) {
			element.classList.add('cc_cart_zero');
		} else {
			element.classList.remove('cc_cart_zero');
		}
	});
}

/**
 * Update the saved items empty class based on saved items count
 * @param {number} savedItemsCount - Current number of saved items
 */
function updateSavedItemsEmptyClass(savedItemsCount) {
	const savedBody = document.querySelector('.cc-saves .cc-body');
	const emptyState = document.getElementById('cc-empty-saved-items');

	if (!savedBody) return;

	if (savedItemsCount === 0) {
		savedBody.classList.add('cc-empty');
		// Show the empty state message
		if (emptyState) {
			emptyState.classList.remove('cc-hidden');
		}
	} else {
		savedBody.classList.remove('cc-empty');
		// Hide the empty state message
		if (emptyState) {
			emptyState.classList.add('cc-hidden');
		}
	}
}

/**
 * Refresh cart state from server
 */
async function refreshCartFromServer() {
	// If a refresh is already running, request a follow-up pass (so changes made
	// after the in-flight read began are captured) and await the chain so the
	// caller observes up-to-date state. No extra fetch in the common,
	// non-overlapping case — overlapping calls are coalesced, not multiplied.
	if (refreshInFlight) {
		refreshQueued = true;
		await refreshInFlight;
		// The settled refresh may have spawned the follow-up we requested; await it.
		while (refreshInFlight) {
			await refreshInFlight;
		}
		return;
	}

	// Disable cart during sync to prevent interaction glitches
	const { state } = store('caddy/cart');
	state.isLoading = true;

	refreshInFlight = (async () => {
		try {
			// Re-run while another refresh was requested mid-flight, so the final
			// read always reflects the latest server state.
			do {
				refreshQueued = false;
				await runCartRefresh();
			} while (refreshQueued);
		} finally {
			refreshInFlight = null;
			// Re-enable cart after sync
			state.isLoading = false;
		}
	})();

	await refreshInFlight;
}

/**
 * Perform a single cart read from the Store API and write it into state.
 */
async function runCartRefresh() {
	const { state } = store('caddy/cart');
	try {
		// Use Store API instead of custom Caddy endpoint (which returns empty state)
		const response = await storeApiFetch('cart');

		const cartData = await response.json();

		if (response.ok && cartData) {

			// Convert Store API format to Caddy format
			if (cartData.items && cartData.items.length > 0) {
				const caddyItems = cartData.items.map(item => {
					const newItem = convertStoreApiItemToCaddyFormat(item);
					// Preserve isUpdating flag from existing items during refresh
					const existing = state.items.find(old => old.cartKey === newItem.cartKey);
					if (existing && existing.isUpdating) {
						newItem.isUpdating = true;
					}
					return newItem;
				});

				// Filter out items that are still being removed by concurrent requests
				const filteredItems = pendingRemovals.size > 0
					? caddyItems.filter(item => !pendingRemovals.has(item.cartKey))
					: caddyItems;

				// Always update after coupon operations or explicit refresh calls
				state.items = filteredItems;
				state.cartCount = filteredItems.reduce((total, item) => total + item.quantity, 0);
				state.isItemSingular = cartData.items_count === 1;
				updateCartTotals(state, cartData);
				state.coupons = cartData.coupons || [];
				state.discountTotal = getStoreApiDiscountTotal(state, cartData);
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);

				// Update applied coupons display to refresh discount amount
				updateAppliedCouponsDisplay(cartData.coupons || []);
			} else {
				// Cart is empty on server
				state.items = [];
				state.cartCount = 0;
				updateCartEmptyClass(state.cartCount);
				updateCartWidgetCount(state.cartCount);
				state.cartSubtotal = 0;
			state.cartSubtotalFormatted = formatPrice(0);
				state.cartTotal = '$0.00';

				// Update widget counts
				updateCartWidgetCount(state.cartCount);
			}
		}
	} catch (error) {
		console.warn('Caddy: Failed to refresh cart from server:', error);
	}
}

// Track if recommendations prefetch has been scheduled
let recommendationsPrefetchScheduled = false;

/**
 * Initialize recommendations functionality
 * This is a wrapper that calls the module version if loaded, otherwise uses Interactivity API
 */
function initializeRecommendations() {
	// For Interactivity API mode, check if we have recommendations in state
	const { state } = store('caddy/cart');
	if (state.recommendations !== undefined) {
		// Always refresh from server (recommendations are lazy loaded)
		refreshRecommendationsFromServer(state);
		return;
	}

	// Fallback to legacy module if loaded
	if (window._caddyInitializeRecommendations) {
		window._caddyInitializeRecommendations();
		return;
	}
}

/**
 * Schedule background prefetch of recommendations during idle time
 * This avoids blocking the initial page render with expensive DB queries
 */
function scheduleRecommendationsPrefetch() {
	if (recommendationsPrefetchScheduled) return;
	recommendationsPrefetchScheduled = true;

	const { state } = store('caddy/cart');

	// Only prefetch if using Interactivity API and recommendations aren't loaded yet
	if (state.recommendations === undefined) return;
	if (state.recommendations.length > 0) {
		state.recommendationsLoading = false;
		return; // Already have recommendations
	}

	// Use requestIdleCallback for background prefetch, with setTimeout fallback
	const prefetch = () => {
		refreshRecommendationsFromServer(state);
	};

	if ('requestIdleCallback' in window) {
		requestIdleCallback(prefetch, { timeout: 2000 }); // Max 2s delay
	} else {
		setTimeout(prefetch, 100); // Fallback for Safari
	}
}

/**
 * Refresh recommendations from server based on current cart items
 * Updates the state.recommendations array for Interactivity API
 */
async function refreshRecommendationsFromServer(state) {
	// Skip if recommendations not enabled (but don't check container - it might be hidden)
	// The container visibility is handled by Interactivity API reactivity

	// Get the last product ID and cart product IDs for exclusion
	let productId = null;
	let cartProductIds = [];

	if (state.items && state.items.length > 0) {
		// Skip bundled child items — use the last top-level or bundle container item
		const topLevelItems = state.items.filter(item => !item.isBundledItem);
		const lastProduct = topLevelItems.length > 0
			? topLevelItems[topLevelItems.length - 1]
			: state.items[state.items.length - 1];
		productId = lastProduct.productId;
		cartProductIds = state.items.map(item => item.productId);
	}

	// Show loading state
	state.recommendationsLoading = true;
	state.recommendationIndex = 0;

	try {
		// Build the API URL
		let apiUrl;
		if (productId) {
			let path = `recommendations/${productId}?limit=3`;
			if (cartProductIds.length > 0) {
				path += `&exclude=${cartProductIds.join(',')}`;
			}
			apiUrl = getCaddyRestUrl(path);
		} else {
			// Fallback to popular products
			apiUrl = getStoreApiUrl('products?per_page=3&orderby=popularity&_fields=id,name,permalink,prices,images,type');
		}

		const response = await fetch(apiUrl, {
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (!response.ok) {
			throw new Error('Failed to fetch recommendations');
		}

		const data = await response.json();
		const products = data.products || data;

		if (!products || products.length === 0) {
			state.recommendations = [];
			state.recommendationsLoading = false;
			state.showRecommendations = false;
			return;
		}

		// Format products for Interactivity API template
		const formattedProducts = products.map(product => {
			const salePrice = product.prices?.sale_price ? convertStoreApiPrice(product.prices.sale_price) : null;
			const regularPrice = product.prices?.regular_price ? convertStoreApiPrice(product.prices.regular_price) : null;
			const currentPrice = salePrice || regularPrice;
			const isOnSale = salePrice && salePrice < regularPrice;

			const rawImageUrl = product.images && product.images[0]
				? (product.images[0].thumbnail || product.images[0].src)
				: '';
			const imageUrl = rawImageUrl ? rawImageUrl.replace(/([^:])\/\/+/g, '$1/') : getWooCommercePlaceholderImage();

			const isVariable = product.type === 'variable';
			const isGrouped = product.type === 'grouped';
			const isSimple = !isVariable && !isGrouped;

				const decodedProductName = decodeHTMLEntitiesDeep(product.name || '');

				return {
					id: product.id,
					name: decodedProductName,
					permalink: product.permalink,
				price: formatPrice(currentPrice),
				regularPrice: isOnSale ? formatPrice(regularPrice) : '',
				isOnSale: isOnSale,
				image: imageUrl,
				type: product.type,
				isVariable: isVariable,
				isGrouped: isGrouped,
				isSimple: isSimple,
				buttonText: (isVariable
					? getRecommendationLabel('seeOptions', 'Select options')
					: (isGrouped ? getRecommendationLabel('viewProducts', 'View products') : getRecommendationLabel('addToCart', 'Add to cart'))),
				isAdding: false
			};
		});

		state.recommendations = formattedProducts;
		state.showRecommendations = formattedProducts.length > 0;
	} catch (error) {
		console.error('Error loading recommendations:', error);
		state.recommendations = [];
		state.showRecommendations = false;
	} finally {
		state.recommendationsLoading = false;
	}
}


/**
 * Calculate formatted remaining amount for free shipping
 */
function calculateFreeShippingRemainingFormatted() {
	const freeShippingContainer = document.querySelector('.cc-fs[data-free-shipping-amount]');
	if (!freeShippingContainer) {
		return '';
	}

	const freeShippingAmount = parseFloat(freeShippingContainer.dataset.freeShippingAmount);
	const { state } = store('caddy/cart');
	const currentTotal = state.cartSubtotal || 0;
	const remaining = Math.max(0, freeShippingAmount - currentTotal);

	return formatPrice(remaining);
}

/**
 * Calculate free shipping progress percentage
 */
function calculateFreeShippingPercentage() {
	const freeShippingContainer = document.querySelector('.cc-fs[data-free-shipping-amount]');
	if (!freeShippingContainer) return '0%';

	const freeShippingAmount = parseFloat(freeShippingContainer.dataset.freeShippingAmount);
	const { state } = store('caddy/cart');
	const currentTotal = state.cartSubtotal || 0;

	const percentage = Math.min((currentTotal / freeShippingAmount) * 100, 100);
	return percentage + '%';
}

/**
 * Check if free shipping threshold is achieved
 */
function calculateFreeShippingAchieved() {
	const freeShippingContainer = document.querySelector('.cc-fs[data-free-shipping-amount]');
	if (!freeShippingContainer) return false;

	const freeShippingAmount = parseFloat(freeShippingContainer.dataset.freeShippingAmount);
	const { state } = store('caddy/cart');
	const currentTotal = state.cartSubtotal || 0;

	return currentTotal >= freeShippingAmount;
}

// Initialize cross-tab synchronization and load cart data after all functions are defined
document.addEventListener('DOMContentLoaded', async () => {
	setupCrossTabSync();

	// Check if using Interactivity API for recommendations (state.recommendations exists)
	// If so, skip loading the legacy module - Interactivity API handles everything
	const cartStoreCheck = store('caddy/cart');
	const usingInteractivityRecommendations = cartStoreCheck.state.recommendations !== undefined;

	// Only load legacy Recommendations module if NOT using Interactivity API
	// and legacy container exists
	const legacyRecommendationsExists = document.getElementById('cc-store-api-recommendations');
	if (!usingInteractivityRecommendations && legacyRecommendationsExists) {
		try {
			const { initializeRecommendationsModule } = await import(/* webpackMode: "eager" */ './modules/recommendations/index.js');
			const recsModule = initializeRecommendationsModule({
				updateCartTotals,
				updateAppliedCouponsDisplay
			});
			// Override global initializeRecommendations with module version
			window._caddyInitializeRecommendations = recsModule.initializeRecommendations;
		} catch (error) {
			// Recommendations module failed to load
			console.warn('Caddy: cart operation failed:', error);
		}
	}

	// Lazy-load Save for Later module if SFL elements are present in DOM
	const sflTabExists = document.querySelector('.cc-save-nav, #cc-saves, .save_for_later_btn');

	if (sflTabExists) {
		try {
			const { initializeSaveForLater } = await import('./modules/sfl-module.js');
			initializeSaveForLater({
				updateCartTotalsFromItems,
				removeItemFromServer,
				refreshCartFromServer,
				updateCartEmptyClass,
				updateCartWidgetCount,
				updateCartTotals,
				updateAppliedCouponsDisplay,
				initializeRecommendations
			});
		} catch (error) {
			// Save for Later module failed to load
			console.warn('Caddy: cart operation failed:', error);
		}
	}

	// Don't clear the PHP-provided state - trust it as the initial state
	// The Interactivity API has already hydrated with state from wp_interactivity_state()
	// We'll fetch from Store API to ensure freshness, but only update if values changed

	// Update widget counts from PHP state
	const cartStore = store('caddy/cart');
	updateCartWidgetCount(cartStore.state.cartCount || 0);

	// Clear any localStorage/sessionStorage that might interfere
	try {
		localStorage.removeItem('caddy_cart_update');
		localStorage.removeItem('caddy_last_cart_update');
		localStorage.removeItem('wc_cart_hash');
		// Clear WooCommerce fragments cache
		if (window.wc_cart_fragments_params && window.wc_cart_fragments_params.fragment_name) {
			sessionStorage.removeItem(window.wc_cart_fragments_params.fragment_name);
		}
	} catch (e) {
	}


	// Load initial cart data from Store API
	// Set flag immediately to prevent duplicate fetch if cart opens during initial load
	initialCartLoadComplete = true;

	// Trust the PHP-seeded state when the cart-hash cookie confirms it's current —
	// cookies bypass page caches, so a mismatch means the rendered HTML (and its
	// seeded cart state) came from a stale cache entry and must be refreshed
	const seededCartFresh = isSeededCartFresh(cartStore.state);

	try {
		const cartResponse = seededCartFresh ? null : await storeApiFetch('cart');

		if (cartResponse && cartResponse.ok) {
			const cartData = await cartResponse.json();

			// Update cart state with Store API data
			const cartStore = store('caddy/cart');
			if (cartData.items && cartData.items.length > 0) {
				// Convert Store API format to Caddy format
				const caddyItems = convertCartItems(cartData.items);

				// Debug the converted items
				caddyItems.forEach((item, index) => {
				});

				// Only update items if they've changed to prevent unnecessary re-renders
				const currentItems = cartStore.state.items;
				const itemsChanged = currentItems.length !== caddyItems.length ||
					caddyItems.some((newItem, index) => {
						const currentItem = currentItems[index];
						return !currentItem ||
							currentItem.cartKey !== newItem.cartKey ||
							currentItem.quantity !== newItem.quantity ||
							currentItem.lineTotal !== newItem.lineTotal;
							// Don't compare image - URL format differences don't matter
					});

				if (itemsChanged) {
					cartStore.state.items = caddyItems;
				}

				// Only update cart totals if they've changed to prevent unnecessary re-renders
				const newCartCount = cartData.items_count;
				const newCartTotal = cartData.totals.total_formatted || formatPrice(convertStoreApiPrice(cartData.totals.total_price));
				const newCartSubtotal = getStoreApiSubtotal(cartStore.state, cartData);
				const newCartSubtotalFormatted = formatPrice(newCartSubtotal);
				const newDiscountTotal = getStoreApiDiscountTotal(cartStore.state, cartData);

				const totalsChanged =
					cartStore.state.cartCount !== newCartCount ||
					cartStore.state.cartTotal !== newCartTotal ||
					cartStore.state.cartSubtotal !== newCartSubtotal ||
					cartStore.state.discountTotal !== newDiscountTotal;

				if (totalsChanged) {
					cartStore.state.cartCount = newCartCount;
					cartStore.state.cartTotal = newCartTotal;
					cartStore.state.cartSubtotal = newCartSubtotal;
					cartStore.state.cartSubtotalFormatted = newCartSubtotalFormatted;
					cartStore.state.discountTotal = newDiscountTotal;
					// Don't update shippingEligibleTotal - keep the PHP value
				}

				// Only update coupons if they've changed
				const couponsChanged = JSON.stringify(cartStore.state.coupons) !== JSON.stringify(cartData.coupons || []);
				if (couponsChanged) {
					cartStore.state.coupons = cartData.coupons || [];
				}

				// Only update DOM if data actually changed
				if (itemsChanged || totalsChanged || couponsChanged) {
					// Update widget counts and compass count
					updateCartWidgetCount(cartStore.state.cartCount);
					updateSavedItemsWidgetCount(cartStore.state.savedItems.length);

					// Update applied coupons display
					updateAppliedCouponsDisplay(cartData.coupons || []);

					// Update DOM elements with proper CSS classes based on sale status
					setTimeout(() => updateCartItemSaleClasses(caddyItems), 100);
				}

			} else if (cartStore.state.items && cartStore.state.items.length > 0) {
				// Server cart is empty but the seeded state has items — stale cached
				// page or post-checkout navigation. Clear the drawer to match reality.
				cartStore.state.items = [];
				cartStore.state.cartCount = 0;
				cartStore.state.isItemSingular = false;
				cartStore.state.cartSubtotal = 0;
				cartStore.state.cartSubtotalFormatted = formatPrice(0);
				cartStore.state.cartTotal = formatPrice(0);
				cartStore.state.coupons = [];
				cartStore.state.discountTotal = 0;
				updateCartEmptyClass(0);
				updateCartWidgetCount(0);
				updateAppliedCouponsDisplay([]);
			}
		}
	} catch (error) {
		console.warn('Caddy: initial cart refresh failed:', error);
	}

	// Initialize coupon drawer toggle
	initializeCouponDrawer();

	// Initialize coupon form submission (pass updateCartTotals function)
	initializeCouponForm(updateCartTotals);
});


/**
 * Update cart item visibility for sale elements after state changes
 * @param {string} cartKey The cart key of the item to update
 * @param {Object} item The updated cart item data
 */
function updateCartItemVisibility(cartKey, item) {

	// Find the cart item container for this specific item
	const cartItemContainers = document.querySelectorAll('.cc-cart-item');
	let targetContainer = null;

	// Find the container that matches this cart key
	cartItemContainers.forEach((container) => {
		const containerCartKey = container.getAttribute('data-wp-key');
		if (containerCartKey === cartKey) {
			targetContainer = container;
		}
	});

	if (!targetContainer) {
		return;
	}

	// Find sale-related elements in this container
	const saleWrapper = targetContainer.querySelector('.cc-sale-price-wrapper');
	const savingsDiv = targetContainer.querySelector('.cc_saved_amount');

	// Update visibility based on item sale status
	if (saleWrapper) {
		if (item.showSalePrice) {
			saleWrapper.classList.remove('cc-hidden');
		} else {
			saleWrapper.classList.add('cc-hidden');
		}
	}

	if (savingsDiv) {
		if (item.showSavings) {
			savingsDiv.classList.remove('cc-hidden');
		} else {
			savingsDiv.classList.add('cc-hidden');
		}
	}
}

/**
 * Update cart item DOM elements to show/hide sale elements based on actual sale status
 * @param {Array} cartItems Array of cart items with pricing info
 */
function updateCartItemSaleClasses(cartItems) {

	// Find all cart item containers
	const cartItemContainers = document.querySelectorAll('.cc-cart-item');

	cartItemContainers.forEach((container, index) => {
		if (index >= cartItems.length) return;

		const item = cartItems[index];

		// Find sale-related elements in this container
		const saleWrapper = container.querySelector('.cc-sale-price-wrapper');
		const savingsDiv = container.querySelector('.cc_saved_amount');

		// Show or hide sale elements based on actual sale status
		const isActuallyOnSale = item.isOnSale && item.savingsPercentage > 0;

		if (saleWrapper) {
			if (isActuallyOnSale) {
				saleWrapper.style.display = '';
			} else {
				saleWrapper.style.display = 'none';
			}
		}

		if (savingsDiv) {
			if (isActuallyOnSale) {
				savingsDiv.style.display = '';
			} else {
				savingsDiv.style.display = 'none';
			}
		}
	});
}


/**
 * Vanilla JavaScript slideUp animation (jQuery-like)
 */
// UI utility functions and coupon management now imported from core modules above

// Export core functions for optional modules
export {
	updateCartTotalsFromItems,
	removeItemFromServer,
	refreshCartFromServer,
	updateCartEmptyClass,
	updateCartWidgetCount,
	updateCartTotals,
	updateAppliedCouponsDisplay,
	initializeRecommendations
};
